import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

// Imperative handle the parent uses to drive the terminal from SSE events.
// Kept imperative (rather than a "lines" prop) so high-frequency streaming
// writes go straight to xterm without re-rendering React on every chunk.
export interface TerminalHandle {
  // Print a "$ <command>" prompt line before a command's output.
  writeCommand: (cmd: string) => void;
  // Append a raw output chunk exactly as the container produced it.
  write: (data: string) => void;
  // Print a dim/red exit line after a command finishes.
  writeExit: (exitCode: number | null | undefined, durationSeconds: number | undefined, timedOut: boolean | undefined) => void;
  clear: () => void;
}

// ANSI helpers
const RESET = '\x1b[0m';
const c = (code: string, s: string) => `\x1b[${code}m${s}${RESET}`;

interface Props {
  className?: string;
}

const CapstoneTerminal = forwardRef<TerminalHandle, Props>(({ className }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);

  useEffect(() => {
    const term = new Terminal({
      convertEol: true,            // map LF → CRLF so Unix output renders correctly
      cursorBlink: false,
      disableStdin: true,          // display-only: chat is the only input
      fontSize: 13,
      lineHeight: 1.2,
      fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
      scrollback: 5000,
      theme: {
        background: '#0b0e14',
        foreground: '#c7cad1',
        cursor: '#0b0e14',
        selectionBackground: '#2a3346',
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);

    if (containerRef.current) {
      term.open(containerRef.current);
      // Defer the first fit until the pane has its final size.
      requestAnimationFrame(() => { try { fit.fit(); } catch { /* not visible yet */ } });
    }
    termRef.current = term;
    fitRef.current = fit;

    term.write(c('90', '▸ Terminal — output appears here when Capstone runs commands.\r\n'));

    // Refit on container resize (pane drag, window resize, sidebar toggle).
    const ro = new ResizeObserver(() => { try { fit.fit(); } catch { /* ignore */ } });
    if (containerRef.current) ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    writeCommand: (cmd: string) => {
      termRef.current?.write(`\r\n${c('1;32', '$')} ${c('1', cmd)}\r\n`);
    },
    write: (data: string) => {
      termRef.current?.write(data);
    },
    writeExit: (exitCode, durationSeconds, timedOut) => {
      const dur = durationSeconds != null ? `${durationSeconds}s` : '';
      if (timedOut) {
        termRef.current?.write(`\r\n${c('33', `▸ timed out after ${dur}`)}\r\n`);
      } else if (exitCode === 0) {
        termRef.current?.write(`\r\n${c('90', `▸ exit 0 · ${dur}`)}\r\n`);
      } else {
        termRef.current?.write(`\r\n${c('31', `▸ exit ${exitCode ?? '?'} · ${dur}`)}\r\n`);
      }
    },
    clear: () => termRef.current?.clear(),
  }), []);

  return <div ref={containerRef} className={className} style={{ height: '100%', width: '100%' }} />;
});

CapstoneTerminal.displayName = 'CapstoneTerminal';

export default CapstoneTerminal;
